import React, { useMemo } from "react";
import {
  Box,
  styled,
  SxProps,
  Popper,
  OutlinedInput,
  ClickAwayListener,
  Fade,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { cacheService, rightService, searchService } from "../../../Core/Api/services";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { SearchResponse } from "../../../Core/Api/Types/search-response";
import { useDispatch, useSelector } from "react-redux";
import {
  GetAssetIdsFilter,
  GetAssetStatusFilter,
  GetClassFilter,
  GetConfidentialityFilter,
  GetCreatedbyFilter,
  GetFilters,
  GetPattern,
  GetProviderIdFilter,
  GetStatusHistoryFilter,
  GetTitleCustomerIdsFilter,
  GetTypeFilter,
  SetLimit,
} from "../../../Redux/Reducers/redux-slice-filters";
import { SearchRequestNative } from "../../../Core/Api/Types/search-request-native";
import { SetSearchPattern } from "../../../Redux/Reducers/redux-slice-filters";
import { throttle } from "throttle-debounce";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { RightType } from "../../../Core/Api/Types/right-type";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { GetTitleRouterPath } from "../../../Utils/helpers";
import { useNavigate } from "react-router";
import { ContractAndRightType } from "../../../Core/Api/Right/right-api-service";
import { GetMetadataByTagName } from "../../../Core/Utils/asset-object-utils";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { useTranslation } from "react-i18next";

export type AutocompleteSearchCatalogProps = {
  sx?: SxProps;
  height?: number;
  width?: number;
  layer: ObjectLayer;
  enableSearchCallback: boolean;
  isForTitleCreation?: boolean;
  query?: string;
  error?: boolean;
  title?: string;
  onSearchBasicCallback?: (data: SearchResponse, pattern: string) => void;
  onSearchContractCallback?: (data: ContractAndRightType[], pattern: string) => void;
  onSearchEnter?: (pattern: string) => void;
  onClear?: () => void;
  onChangeQuery?: (pattern: string) => void;
  onValueChange?: (value: string) => void;
  onValueProposalClick?: (value: DbAssetObjectInfos) => void;
  context?: "Catalog" | "Contract" | "ExpertSearch" | "Selection";
  placeholderText?: string;
  withPortal?: boolean;
  triggerSearchFromOutside?: number | undefined;
  onError?: () => void;
  logQuery?: boolean;
};

const AutocompleteSearchCatalog = React.forwardRef<HTMLDivElement | null, AutocompleteSearchCatalogProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const {
      layer,
      height,
      error,
      width,
      title,
      query,
      enableSearchCallback,
      isForTitleCreation,
      context = "Catalog",
      placeholderText = t("field:placeholder.searchByTitle"),
      onSearchBasicCallback,
      onSearchContractCallback,
      onSearchEnter,
      onClear,
      onChangeQuery,
      onValueChange,
      onValueProposalClick,
      withPortal = false,
      triggerSearchFromOutside = undefined,
      onError,
      logQuery = false,
      ...otherProps
    } = props;
    const $searchRef = React.useRef<HTMLInputElement>(undefined);
    const [popperOpen, setPopperOpen] = React.useState(false);
    const [searchFieldWidth, setSearchFieldWidth] = React.useState(0);
    const [popperAnchorEl, setPopperAnchorEl] = React.useState<HTMLInputElement>();
    const [isLoading, setIsLoading] = React.useState(false);
    const filters = useSelector(GetFilters);
    const typeFilter = useSelector(GetTypeFilter);
    const classFilter = useSelector(GetClassFilter);
    const statusFilter = useSelector(GetAssetStatusFilter);
    const confidentialityFilter = useSelector(GetConfidentialityFilter);
    const createdbyFilter = useSelector(GetCreatedbyFilter);
    const providerIdFilter = useSelector(GetProviderIdFilter);
    const assetIdsFilter = useSelector(GetAssetIdsFilter);
    const statusHistoryFilter = useSelector(GetStatusHistoryFilter);
    const titleCustomerIdsFilter = useSelector(GetTitleCustomerIdsFilter);

    const MIN_CHARS_FOR_SEARCH = 2;

    const onClickAway = React.useCallback(() => {
      setPopperOpen(false);
    }, []);
    const [value, setValue] = React.useState("");
    const patternRedux = useSelector(GetPattern);
    const [propositions, setPropositions] = React.useState<
      Omit<{ id: number; name: string | undefined; label: string | undefined }, "index">[]
    >([]);

    const neededMetas = useMemo(
      () =>
        cacheService
          .GetMetadata()
          .filter(
            (x) => x.tagName && ["titleType", "serieSeasonEpisodeTotalNumber"].includes(x.tagName)
          ) as DbMetadataFieldInfos[],
      []
    );

    // Abort
    const abortCtrl = new AbortController();

    // Redux
    const dispatch = useDispatch();
    const metadataFields = React.useMemo(() => cacheService.GetMetadata(), []);

    const navigate = useNavigate();

    // On click on proposal result
    const onPoperClick = React.useCallback(
      (obj: any) => {
        if (context === "Contract") {
          handleContractsSearch(obj);
          return;
        }

        if (context === "ExpertSearch") {
          setValue(obj.name);
          onClickAway();
          return;
        }

        if (context === "Selection") {
          handleNativeSearch(obj.label, obj.customerId);
          return;
        }

        if (onValueProposalClick) onValueProposalClick(obj);
        else navigate(GetTitleRouterPath() + "/" + obj.id);

        setPopperOpen(false);
      },
      [onValueProposalClick, setPopperOpen, context]
    );

    const handleContractsSearch = (title: any) => {
      if (!enableSearchCallback) {
        dispatch(SetSearchPattern(title.name));
        return;
      }

      setPopperOpen(false);
      onSearchEnter && onSearchEnter(title.name);

      rightService
        .GetRightContractByTitleId(title.id, RightType.All, metadataFields, !(context === "Contract"))
        .then(function (rep) {
          setValue("");
          onSearchContractCallback && onSearchContractCallback(rep, title.name);
        })
        .catch((err) => {
          onError && onError();
          setIsLoading(false);
        });
    };

    // Go search
    const handleNativeSearch = (pattern: string, customerId: string | undefined = undefined) => {
      if (context === "Contract") {
        if (propositions && propositions.length > 0) {
          handleContractsSearch(propositions[0]);
        }
        return;
      }

      // Save pattern in redux
      dispatch(SetSearchPattern(pattern));
      dispatch(SetLimit(null));

      if (!enableSearchCallback) {
        return;
      }

      setIsLoading(true);
      setPopperOpen(false);
      onSearchEnter && onSearchEnter(pattern);

      searchService
        .SearchBasic(
          new SearchRequestNative({
            logQuery: logQuery,
            outputLayer: layer,
            hideTitleWithoutAsset: false,
            pattern: pattern,
            metadata: filters,
            classifications: classFilter,
            types: typeFilter,
            statuses: statusFilter,
            statusHistory: statusHistoryFilter,
            onlyConfidential: confidentialityFilter,
            titleCustomerIds: customerId ? [customerId] : titleCustomerIdsFilter,
            assetIds: assetIdsFilter,
            createdBy: createdbyFilter?.ids,
            providerIds: providerIdFilter?.ids,
          })
        )
        .then(function (rep) {
          onSearchBasicCallback && onSearchBasicCallback(rep, pattern);
          setIsLoading(false);
        })
        .catch((err) => {
          onError && onError();
          setIsLoading(false);
        });
    };

    React.useEffect(() => {
      onValueChange && onValueChange(value);
    }, [value]);

    React.useEffect(() => {
      if (triggerSearchFromOutside === undefined || triggerSearchFromOutside === 0) return;

      handleNativeSearch(value ?? "*");
    }, [triggerSearchFromOutside]);

    const throttledTypeheadSearch = React.useMemo(
      () =>
        throttle(
          GlobalConst.SEARCH_THROTTLE_DELAY_MS, // Wait half sec
          (searchValue: string) => {
            setIsLoading(true);

            // Call search to have autocomplition propositions
            searchService
              .SearchBasic(
                new SearchRequestNative({
                  outputLayer: ObjectLayer.Title, // Do basic quick search on titles
                  hideTitleWithoutAsset: false,
                  pattern: searchValue,
                }),
                abortCtrl.signal
              )
              .then(function (rep) {
                if (rep !== null && rep.results !== null) {
                  const movieCardsData: {
                    id: number;
                    name: string | undefined;
                    label: string | undefined;
                    customerId: string | null;
                  }[] = rep.results.map((title) => ({
                    id: title.id,
                    name: `${title.titleCustomerId ? `#${title.titleCustomerId} - ` : ""}${title.name}${
                      GetMetadataByTagName(title, "titleType", neededMetas)?.value === "Season"
                        ? ` - ${
                            GetMetadataByTagName(title, "serieSeasonEpisodeTotalNumber", neededMetas)?.value || 0
                          } ep`
                        : ""
                    }`,
                    label: title.name,
                    customerId: title.titleCustomerId,
                  }));

                  setPropositions(movieCardsData);
                  setPopperOpen(true);
                  setIsLoading(false);

                  console.log("search ok for pattern = " + searchValue);
                }
              })
              .catch((ex) => {
                console.warn("search basic typehead catch()");
              });
          },
          {
            noLeading: true,
          }
        ),
      []
    );

    // On typing on search bar
    const onChange: React.ChangeEventHandler<HTMLInputElement> = React.useCallback((e) => {
      setPropositions([]);
      onChangeQuery && onChangeQuery(e.currentTarget.value);

      if (e === undefined || e.currentTarget === undefined || e.currentTarget.value === undefined) return;

      let v = e.currentTarget.value.toString();
      setValue(v);

      if (e.currentTarget.value.length >= MIN_CHARS_FOR_SEARCH) {
        throttledTypeheadSearch(e.currentTarget.value);
      }
    }, []);

    // On Enter on search bar
    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        if (event.target.defaultValue === "" || event.target.defaultValue === " ") {
          // Save pattern in redux
          dispatch(SetSearchPattern(""));
          return;
        }

        handleNativeSearch(event.target.defaultValue);
        abortCtrl.abort();
      }
    };

    React.useEffect(() => {
      if (!$searchRef.current) return;
      setSearchFieldWidth($searchRef.current.clientWidth);
      setPopperAnchorEl($searchRef.current);

      if (patternRedux !== "") setValue(patternRedux);
    }, [patternRedux]);

    return (
      <ClickAwayListener onClickAway={onClickAway}>
        <StyledSearch ref={ref} isVisible={true} width={width}>
          {isLoading && (
            <CircularProgress
              size={16}
              sx={{
                position: "absolute",
                top: 8,
                left: 188,
                zIndex: 1,
              }}
            />
          )}
          <OutlinedInput
            placeholder={title ? title : placeholderText}
            ref={$searchRef}
            value={query ? query : value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            sx={{ width: "100%", paddingRight: 0 }}
            error={error ?? false}
            endAdornment={
              <InputAdornment position="end">
                {value && (
                  <IconButton
                    aria-label="Clear"
                    onClick={() => {
                      setValue("");
                      setPopperOpen(false);
                      onClear && onClear();
                    }}
                    size="small"
                  >
                    <CloseRoundedIcon />
                  </IconButton>
                )}
                {triggerSearchFromOutside === undefined && (
                  <IconButton size="small" onClick={() => handleNativeSearch(value)}>
                    <SearchIcon />
                  </IconButton>
                )}
              </InputAdornment>
            }
          />
          <Popper
            sx={isForTitleCreation ? { zIndex: 100000, position: "fixed !important" } : { zIndex: 100000 }}
            open={popperOpen}
            disablePortal={!withPortal}
            placement="bottom-start"
            {...(popperAnchorEl ? { anchorEl: popperAnchorEl } : {})}
            transition
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <StyledBox {...otherProps} width={searchFieldWidth}>
                  <StyledWrapper>
                    <ul>
                      {propositions.length > 0 &&
                        propositions?.map((result) => (
                          <StyledLi key={result.id} onClick={() => onPoperClick(result)}>
                            {result.name}
                          </StyledLi>
                        ))}
                      {propositions.length === 0 && <StyledLi key="empty">{t("modal:body.noResultsFound")}</StyledLi>}
                    </ul>
                  </StyledWrapper>
                </StyledBox>
              </Fade>
            )}
          </Popper>
        </StyledSearch>
      </ClickAwayListener>
    );
  }
);

export default AutocompleteSearchCatalog;

const StyledLi = styled("li")(({ theme }) => ({
  margin: 2,
  padding: 5,
  cursor: "pointer",
  borderRadius: "5px",
  "&:hover": {
    backgroundColor: theme.palette.backgroundAlpha.border,
  },
}));

const StyledBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "sx" && prop !== "width",
})<{ width: number }>(({ theme, width }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: 12,
  //   width: width > 0 ? `${width}px` : "100%",
  width: "305px",
  borderRadius: 12,
  background: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.backgroundAlpha.border}`,
  overflow: "hidden",
  padding: theme.spacing(1.5),
  boxShadow: theme.shadows[6],
  height: "auto",
  maxHeight: "300px",
  "& ul": {
    margin: 0,
  },
}));

const StyledWrapper = styled(Box)(({ theme }) => ({
  overflow: "auto",
  ...theme.helpers(theme).scrollBar,
}));
const StyledSearch = styled(Box, { shouldForwardProp: (prop) => prop !== "isVisible" && prop !== "width" })<{
  isVisible: boolean;
  width?: number;
}>(({ theme, isVisible, width }) => ({
  position: "relative",
  zIndex: 2,
  transition: "opacity .6s cubic-bezier(0.22, 1, 0.36, 1)",
  willChange: "opacity",
  width: width ? `${width}px` : "auto",
  ...(!isVisible && {
    opacity: 0,
    pointerEvents: "none",
  }),
}));

const StyledOutlinedInput = styled(OutlinedInput)<{ value: string }>(({ theme, value }) => ({
  paddingRight: 0,
  svg: {
    position: "absolute",
    top: 10,
    right: theme.spacing(1.5),
    pointerEvents: "none",
    color: theme.palette.common.white,
  },
  ".MuiInputBase-input": {
    width: 20,
    transition: "width .15s  cubic-bezier(0.65, 0, 0.35, 1), paddingRight .15s  cubic-bezier(0.65, 0, 0.35, 1)",
    "&::placeholder": {
      opacity: 0,
    },
    "&:focus": {
      width: 200,
      paddingRight: theme.spacing(4),
      "&:placeholder": {
        opacity: 0,
      },
    },
    ...(value?.length > 0 && {
      width: 200,
      paddingRight: theme.spacing(4),
      "&:placeholder": {
        opacity: 0,
      },
    }),
  },
}));
